/**
 * Amplifier profiles -- group 09 (American valve and hybrid specialists).
 *
 * The point of this group is that "tube amplifier" is not one algorithm. It
 * contains a transformer-coupled push-pull tetrode design, two amplifiers with
 * no audio-frequency output transformer that arrive there by completely
 * different means, two hybrids that place the valve stage in different parts of
 * the signal path, a differential valve line stage, a single-ended one, and a
 * fully balanced solid-state amplifier from a house better known for valves.
 * Output impedance, transformer behaviour, feedback depth and harmonic
 * structure differ across all eight, so no two compile to the same DSP.
 *
 * Confidence is 'ESTIMATED' throughout. These manufacturers publish topology,
 * form factor and rated power, but rarely a complete distortion, noise and
 * bandwidth specification; every provenanceNote below separates the recalled
 * published facts from the class-typical inferences. Prices are approximate
 * because all of these are dealer-quoted, low-volume products.
 */

import type { AmpProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeAmp, usd } from '../kit';

export const AMPS_09: AmpProfile[] = [
  makeAmp({
    mfr: 'Balanced Audio Technology',
    model: 'VK-255SE',
    price: usd(8500, 'APPROXIMATE', 2019, 'Approximate US retail; the SE version was offered with capacitor upgrade options that moved the price.'),
    year: 2010,
    country: 'United States',
    topology: 'solid-state-ab',
    form: 'stereo-power',
    devices: 'Fully balanced dual-mono complementary bipolar output with a large oil-capacitor supply reservoir',
    power: { watts8: 150, watts4: 300, confidence: 'ESTIMATED' },
    gainDb: 26,
    thdPercent: 0.08,
    snrDb: 103,
    bandwidth: [1, 80000],
    dampingFactor: 200,
    feedbackDb: 22,
    crosstalkDb: -95,
    balancedInput: true,
    dynamics: { sagDb: 0.2, sagTimeMs: 90, recoveryMs: 200, thermalDb: 0.15, thermalTimeMs: 7000, slewRateVus: 70, headroomDb: 2.4 },
    clipping: { kind: 'asymmetric', kneeDb: 2.0, stickiness: 0.06 },
    confidence: 'ESTIMATED',
    accent: '#5a6b7c',
    tags: ['fully-balanced', 'dual-mono', 'oil-capacitor-supply'],
    notes:
      'The only solid-state amplifier in this group and the only one whose output impedance is low enough to make the loudspeaker impedance curve essentially irrelevant to frequency response. Because the signal path is differential end to end, even-order products from the two halves cancel and what survives is the odd-order crossover residue of the Class AB output stage, so the modelled harmonic structure is the mirror image of the valve entries around it. The oversized capacitor reservoir is modelled as very low supply sag, which keeps dynamic behaviour flat where the transformer-coupled designs here compress.',
    provenanceNote:
      'Fully balanced dual-mono topology, the oil-capacitor supply and the stereo power-amplifier form are from Balanced Audio Technology published material; rated power is recalled as approximately 150 W into 8 ohms and is not treated as exact. THD, signal-to-noise ratio, bandwidth, damping factor, feedback depth, crosstalk and all dynamic and clipping parameters are class-typical estimates. Price is approximate.',
  }),

  makeAmp({
    mfr: 'Convergent Audio Technology',
    model: 'JL-2 Signature',
    price: usd(22000, 'APPROXIMATE', 2019, 'Approximate US retail; the JL-2 has been offered in several Signature revisions at different prices.'),
    year: 2000,
    country: 'United States',
    topology: 'tube-push-pull',
    form: 'stereo-power',
    devices: 'Parallel push-pull beam-tetrode output with custom wide-bandwidth output transformers and moderate loop feedback',
    power: { watts8: 100, watts4: 100, confidence: 'ESTIMATED' },
    gainDb: 26,
    thdPercent: 0.5,
    snrDb: 88,
    bandwidth: [7, 60000],
    outputTransformer: true,
    transformerLfHz: 9,
    transformerHfHz: 54000,
    dampingFactor: 12,
    feedbackDb: 10,
    crosstalkDb: -70,
    dynamics: { sagDb: 0.9, sagTimeMs: 160, recoveryMs: 380, slewRateVus: 22, headroomDb: 1.3 },
    clipping: { kind: 'soft', kneeDb: 6.5, stickiness: 0.32 },
    confidence: 'ESTIMATED',
    accent: '#a4602f',
    tags: ['parallel-push-pull', 'beam-tetrode', 'wide-band-transformer'],
    notes:
      'Parallel push-pull tetrodes with about ten dB of loop feedback: the push-pull pairing cancels most of the second harmonic, the tetrodes contribute a third harmonic that the feedback then reduces without eliminating, so the residue is odd-dominant and moderate rather than the second-harmonic ramp of a single-ended design. Rated power does not increase into 4 ohms because the output transformer sets the load ratio, which is the behavioural signature that separates every transformer-coupled amplifier here from the solid-state entry. A damping factor near twelve lets the loudspeaker impedance curve modulate response by several tenths of a dB, and the transformer adds core saturation below about 9 Hz and leakage-inductance rolloff above about 54 kHz.',
    provenanceNote:
      'Parallel push-pull valve topology, output-transformer coupling, stereo power-amplifier form and roughly 100 W per channel are from Convergent Audio Technology published descriptions; the output-tube type is deliberately not named because it varies across revisions. THD, signal-to-noise ratio, bandwidth, transformer corner frequencies, damping factor, feedback depth and crosstalk are estimates. Price is approximate.',
  }),

  makeAmp({
    mfr: 'Atma-Sphere',
    model: 'MA-1 Mk 3.3',
    price: usd(18000, 'APPROXIMATE', 2019, 'Approximate US retail per pair; specification level and finish options moved the price.'),
    year: 2012,
    country: 'United States',
    topology: 'tube-otl',
    form: 'monoblock',
    devices: 'Circlotron output using paralleled 6AS7G power triodes, direct-coupled, no output transformer',
    power: { watts8: 140, watts4: 90, confidence: 'ESTIMATED' },
    gainDb: 26,
    thdPercent: 0.9,
    snrDb: 85,
    bandwidth: [2, 150000],
    outputTransformer: false,
    outputImpedanceOhms: 1.8,
    feedbackDb: 3,
    crosstalkDb: -92,
    dynamics: { sagDb: 1.4, sagTimeMs: 200, recoveryMs: 460, slewRateVus: 26, headroomDb: 1.0 },
    clipping: { kind: 'soft', kneeDb: 8, stickiness: 0.38 },
    confidence: 'ESTIMATED',
    accent: '#d0a24a',
    tags: ['circlotron', 'otl', 'low-feedback', 'high-source-impedance'],
    notes:
      'With no output transformer there is no core saturation and no leakage inductance, so the modelled bandwidth is limited only by the valves and the coupling network and runs an octave and a half beyond the transformer-coupled entries. The price of that is source impedance: at 1.8 ohms the loudspeaker impedance curve is printed directly onto the frequency response, and rated power falls rather than rises into 4 ohms because the output triodes are voltage sources being asked for more current. Only three dB of feedback is applied, so the third-harmonic-dominant residue is left largely uncorrected and grows steeply into clipping.',
    provenanceNote:
      'Circlotron output-transformerless topology, paralleled 6AS7G output triodes, monoblock form and roughly 140 W into 8 ohms are from Atma-Sphere published material. The reduced 4 ohm power figure follows from the topology and is derived rather than quoted. THD, signal-to-noise ratio, bandwidth, output impedance, feedback depth, crosstalk and the dynamic and clipping parameters are estimates. Price is approximate.',
  }),

  makeAmp({
    mfr: 'David Berning',
    model: 'ZH-270',
    price: usd(4000, 'DISCONTINUED_USED', 2025, 'Approximate current used-market value; long out of production, and original retail was in a similar region.'),
    year: 1999,
    country: 'United States',
    topology: 'tube-otl',
    form: 'stereo-power',
    devices: 'ZOTL impedance-converting output network switching at radio frequency; no audio-frequency output transformer',
    power: { watts8: 70, watts4: 70, confidence: 'ESTIMATED' },
    gainDb: 27,
    thdPercent: 0.4,
    snrDb: 90,
    bandwidth: [8, 50000],
    outputTransformer: false,
    dampingFactor: 10,
    feedbackDb: 8,
    crosstalkDb: -72,
    dynamics: { sagDb: 0.7, sagTimeMs: 130, recoveryMs: 300, slewRateVus: 24, headroomDb: 1.1 },
    clipping: { kind: 'soft', kneeDb: 6, stickiness: 0.28 },
    confidence: 'ESTIMATED',
    accent: '#7fa6c9',
    tags: ['zotl', 'rf-impedance-conversion', 'no-audio-transformer'],
    notes:
      'Reaches the same "no audio output transformer" endpoint as the Atma-Sphere entry by the opposite route: instead of connecting triodes directly to the load, the output is impedance-converted through a network switching well above the audio band, so the valves always see a near-constant load. The consequences are visible across three parameters at once -- output impedance is roughly half that of the classic circlotron, rated power does not collapse into 4 ohms, and the ultrasonic limit is set by the radio-frequency carrier rather than by transformer leakage inductance, which is why bandwidth is modelled as narrower than the Atma-Sphere despite both being transformerless. Distortion is lower than a direct-coupled OTL because the valves work into an easier load.',
    provenanceNote:
      'The ZOTL radio-frequency impedance-conversion principle, the absence of an audio-frequency output transformer, stereo form and roughly 70 W per channel are from David Berning published material; the constant power into 4 ohms follows from the impedance-conversion topology and is derived. THD, signal-to-noise ratio, bandwidth, damping factor, feedback depth, crosstalk and dynamics are estimates. The price is an approximate used-market figure for a discontinued product.',
  }),

  makeAmp({
    mfr: 'Aesthetix',
    model: 'Atlas',
    price: usd(9500, 'APPROXIMATE', 2019, 'Approximate US retail for the stereo version; Signature and Eclipse upgrade levels cost more.'),
    year: 2009,
    country: 'United States',
    topology: 'hybrid',
    form: 'stereo-power',
    devices: 'Vacuum-tube voltage gain and driver stage into a complementary bipolar output stage',
    power: { watts8: 200, watts4: 400, confidence: 'ESTIMATED' },
    gainDb: 29,
    thdPercent: 0.15,
    snrDb: 95,
    bandwidth: [2, 80000],
    dampingFactor: 90,
    feedbackDb: 14,
    crosstalkDb: -88,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'triode', amount: 0.4, asymmetry: 0.45, bias: 0.5, headroomDb: 13, oversample: 4 },
    dynamics: { sagDb: 0.4, sagTimeMs: 110, recoveryMs: 260, thermalDb: 0.2, thermalTimeMs: 6000, slewRateVus: 55, headroomDb: 2.0 },
    clipping: { kind: 'asymmetric', kneeDb: 3.5, stickiness: 0.15 },
    confidence: 'ESTIMATED',
    accent: '#8f7fd1',
    tags: ['tube-input-stage', 'bipolar-output', 'high-power-hybrid'],
    notes:
      'Two distinct distortion mechanisms live at different signal levels, which is the whole reason to model this separately from either a valve or a solid-state amplifier. All of the voltage gain happens in the valve stage, so its asymmetric second-harmonic-weighted curve is imposed at every listening level; the bipolar output stage only supplies current, so its crossover residue appears just at high current into low impedance. Because feedback is taken around the output stage, damping factor stays near ninety and power doubles into 4 ohms, so the load interaction is solid-state even though the harmonic signature is not.',
    provenanceNote:
      'Hybrid valve-input, bipolar-output topology, stereo power-amplifier form and roughly 200 W into 8 ohms are from Aesthetix published material; the input valve type is deliberately not named. THD, signal-to-noise ratio, bandwidth, damping factor, feedback depth, crosstalk and the split nonlinear model are estimates and modelling choices, not quoted figures. Price is approximate.',
  }),

  makeAmp({
    mfr: 'Doshi Audio',
    model: 'Alaap V3.0',
    price: usd(17500, 'APPROXIMATE', 2020, 'Approximate US retail; configuration with and without the phono section changes the price.'),
    year: 2016,
    country: 'United States',
    topology: 'tube-push-pull',
    form: 'preamp',
    devices: 'Differential vacuum-tube line stage with heavily regulated supplies, capacitor-coupled output, no global feedback',
    power: { outputVrms: 20, confidence: 'ESTIMATED' },
    gainDb: 12,
    thdPercent: 0.06,
    snrDb: 96,
    bandwidth: [2, 120000],
    outputTransformer: false,
    outputImpedanceOhms: 200,
    feedbackDb: 0,
    crosstalkDb: -85,
    dynamics: { sagDb: 0.15, sagTimeMs: 120, recoveryMs: 300, slewRateVus: 30, headroomDb: 8 },
    clipping: { kind: 'soft', kneeDb: 7, stickiness: 0.2, onsetDbFS: -0.5 },
    confidence: 'ESTIMATED',
    accent: '#c98f5a',
    tags: ['line-stage', 'differential', 'zero-feedback', 'regulated-supply'],
    notes:
      'A differential valve line stage with no global feedback: the balanced pairing cancels most of the second harmonic, so what is left is a small odd-order residue that still grows with level rather than sitting at a fixed floor the way a feedback-corrected stage does. Twenty volts of available output swing against a nominal two-volt source means it runs about twenty dB below its own clipping point in normal use, so the profile spends almost all its time in the linear part of the curve. The 200 ohm source impedance is low enough for long interconnects but high enough to matter into a valve power amplifier with a low input impedance.',
    provenanceNote:
      'Differential zero-feedback valve line stage with heavily regulated supplies, and the preamplifier form, are from Doshi Audio published descriptions; the valve complement is deliberately not named. Gain, output swing, THD, signal-to-noise ratio, bandwidth, output impedance and crosstalk are class-typical estimates for a valve line stage of this standing, not quoted specifications. Price is approximate.',
  }),

  makeAmp({
    mfr: 'Backert Labs',
    model: 'Rhumba Extreme',
    price: usd(7500, 'APPROXIMATE', 2021, 'Approximate US retail; the Rhumba Extreme has been revised several times at different prices.'),
    year: 2018,
    country: 'United States',
    topology: 'tube-set',
    form: 'preamp',
    devices: 'Two 6SN7 triodes in a single-ended capacitor-coupled line stage with a fast-discharge power supply',
    power: { outputVrms: 12, confidence: 'ESTIMATED' },
    gainDb: 11,
    thdPercent: 0.2,
    snrDb: 88,
    bandwidth: [6, 70000],
    outputTransformer: false,
    outputImpedanceOhms: 700,
    feedbackDb: 0,
    crosstalkDb: -68,
    dynamics: { sagDb: 0.25, sagTimeMs: 140, recoveryMs: 320, slewRateVus: 18, headroomDb: 7 },
    clipping: { kind: 'soft', kneeDb: 8, stickiness: 0.3, onsetDbFS: -0.5 },
    confidence: 'ESTIMATED',
    accent: '#e0a389',
    tags: ['line-stage', 'single-ended-triode', '6sn7', 'capacitor-coupled'],
    notes:
      'The single-ended counterpart to the Doshi entry, and the contrast is the point: with no push-pull pairing there is nothing to cancel the second harmonic, so distortion is second-harmonic-dominant and rises about one dB per dB of drive from a floor roughly three times higher. A 700 ohm source impedance working through the output coupling capacitor forms a high-pass with whatever follows, and into a 10 kilohm input that corner lands at the bottom of the audible band rather than below it, which is why this profile is load-sensitive in a way the 200 ohm differential stage is not. Bandwidth is correspondingly narrower at both ends.',
    provenanceNote:
      'Single-ended 6SN7 line-stage topology, capacitor-coupled output, the fast-discharge power supply and the preamplifier form are from Backert Labs published material. Gain, output swing, THD, signal-to-noise ratio, bandwidth, output impedance and crosstalk are estimates typical of a single-ended 6SN7 line stage, not quoted specifications. Price is approximate.',
  }),

  makeAmp({
    mfr: 'Vinnie Rossi',
    model: 'L2i-SE',
    price: usd(19995, 'APPROXIMATE', 2020, 'Approximate US retail as configured with the integrated line stage; option modules change the price.'),
    year: 2019,
    country: 'United States',
    topology: 'hybrid',
    form: 'integrated',
    devices: 'Directly-heated triode line stage into a complementary MOSFET output stage, no global feedback',
    power: { watts8: 100, watts4: 170, confidence: 'ESTIMATED' },
    gainDb: 36,
    thdPercent: 0.3,
    snrDb: 92,
    bandwidth: [4, 60000],
    dampingFactor: 40,
    feedbackDb: 0,
    crosstalkDb: -80,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.6, asymmetry: 0.7, bias: 0.75, headroomDb: 11, oversample: 8 },
    dynamics: { sagDb: 0.6, sagTimeMs: 150, recoveryMs: 340, thermalDb: 0.25, thermalTimeMs: 6500, slewRateVus: 35, headroomDb: 1.4 },
    clipping: { kind: 'asymmetric', kneeDb: 5, stickiness: 0.22 },
    confidence: 'ESTIMATED',
    accent: '#b06a4a',
    tags: ['directly-heated-triode', 'mosfet-output', 'zero-global-feedback', 'integrated'],
    notes:
      'The second hybrid in this group and deliberately the opposite of the Aesthetix one: a directly-heated triode does the voltage gain single-ended with no feedback anywhere in the amplifier, and the MOSFET output stage is a follower that adds its own square-law second harmonic on top rather than cancelling anything. With no global loop, output impedance is set by device transconductance alone, so damping factor lands near forty -- an order of magnitude below a feedback-corrected solid-state amplifier and an order of magnitude above the output-transformerless valve designs here. Power increases into 4 ohms but does not double, which is the current-limit signature of an unregulated supply feeding a follower.',
    provenanceNote:
      'Directly-heated triode line stage, MOSFET output stage, absence of global feedback and the integrated form are from Vinnie Rossi published material; rated power is recalled as approximately 100 W into 8 ohms and is not treated as exact. THD, signal-to-noise ratio, bandwidth, damping factor, crosstalk and the nonlinear and dynamic parameters are estimates and modelling choices. Price is approximate.',
  }),
];
